import { useReactiveVar } from "@apollo/client";
import { useFocusEffect, useNavigation } from "expo-router";
import { useCallback, useEffect, useLayoutEffect } from "react";

import useMobileScreen from "@/hooks/useMobileScreen";
import { pageTitleVar } from "@/stores/pageTitle";

/**
 * Hook to set the page title in navigation header and reactive variable
 * @param title - The title to display
 * @param options - Optional configuration
 * @param options.hideOnMobile - If true, sets empty title on mobile devices
 * @param options.skip - If true, skips all title mutations (use in embedded mode
 *   to avoid overwriting the host screen's title with an empty string)
 * @returns The current page title from reactive variable
 */
const useSetPageTitle = (
  title: string,
  options?: { hideOnMobile?: boolean; skip?: boolean }
) => {
  const pageTitle = useReactiveVar(pageTitleVar);
  const navigation = useNavigation();
  const isMobile = useMobileScreen();

  // Determine what title to show in the header
  const headerTitle = options?.hideOnMobile && isMobile ? "" : title;
  const shouldSkip = Boolean(options?.skip);

  useLayoutEffect(() => {
    if (shouldSkip) return;
    navigation.setOptions({ title: headerTitle });
    pageTitleVar(title);
  }, [navigation, title, headerTitle, shouldSkip]);

  useFocusEffect(
    useCallback(() => {
      if (shouldSkip) return;
      navigation.setOptions({ title: headerTitle });
      pageTitleVar(title);
    }, [navigation, title, headerTitle, shouldSkip])
  );

  useEffect(() => {
    if (shouldSkip) return;
    navigation.setOptions({ title: headerTitle });
    pageTitleVar(title);
  }, [navigation, title, headerTitle, shouldSkip]);

  return pageTitle;
};

export default useSetPageTitle;
